import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import type { CostOfDelayItem } from '../types';

interface ScoringViewProps {
  item: CostOfDelayItem;
  itemIndex: number;
  totalItems: number;
  phase: 'valueLost' | 'costToRestart';
  onScoreUpdate: (field: 'valueLost' | 'costToRestart', value: number | null) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
  progress: number;
}

export function ScoringView({
  item,
  itemIndex,
  totalItems,
  phase,
  onScoreUpdate,
  onNext,
  onBack,
  canProceed,
  progress,
}: ScoringViewProps) {
  const [value, setValue] = useState<string>(
    phase === 'valueLost' 
      ? (item.valueLost?.toString() || '')
      : (item.costToRestart?.toString() || '')
  );

  useEffect(() => {
    setValue(
      phase === 'valueLost' 
        ? (item.valueLost?.toString() || '')
        : (item.costToRestart?.toString() || '')
    );
  }, [item, phase]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    
    if (inputValue === '') {
      onScoreUpdate(phase, null);
    } else {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue >= 0) {
        onScoreUpdate(phase, numValue);
      } else {
        onScoreUpdate(phase, null);
      }
    }
  };

  const currentValue = phase === 'valueLost' ? item.valueLost : item.costToRestart;
  const label = phase === 'valueLost' ? 'Value Lost Due to Delay' : 'Cost to Restart or Rework';
  const description = phase === 'valueLost' 
    ? 'The potential value (monetary or otherwise) that would be lost if this item is delayed. This could include opportunity costs, missed deadlines, or lost revenue.'
    : 'The cost (time, money, or effort) required to restart or rework this item if it is delayed. This accounts for context switching, re-learning, or fixing issues that arise from delay.';

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-lg font-semibold text-[var(--text-pri)]">
            Item {itemIndex + 1} of {totalItems}
          </span>
          <span className="text-sm font-medium text-[var(--text-sec)]">
            {Math.round(progress)}% complete
          </span>
        </div>
        <div className="w-full bg-[var(--hover-tint)] rounded-full h-3">
          <div
            className="bg-[var(--slate)] h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Main Scoring Card */}
      <div className="bg-[var(--bg-raised)] shadow-md border border-[var(--rule)] p-8 mb-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
            {item.name}
          </h2>
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
            <span className="px-3 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full font-medium">
              {phase === 'valueLost' ? 'Step 1 of 2' : 'Step 2 of 2'}
            </span>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-lg font-semibold text-[var(--text-pri)] mb-2">
            {label}
          </label>
          <p className="text-sm text-[var(--text-sec)] mb-4">
            {description}
          </p>
          
          <div className="relative">
            <input
              type="number"
              value={value}
              onChange={handleValueChange}
              placeholder="Enter a number..."
              min="0"
              step="0.1"
              className="w-full px-4 py-3 text-2xl border-2 border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            {currentValue !== null && currentValue >= 0 && (
              <div className="mt-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                ✓ Value set: {currentValue}
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)] mb-6">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[var(--text-sec)]">
              <strong>Tip:</strong> {phase === 'valueLost' 
                ? 'Use relative values (e.g., 1-10 scale) or actual monetary impact. Consider opportunity costs, missed deadlines, and lost revenue.'
                : 'Estimate the cost of context switching, re-learning, or fixing issues. This could be time, money, or effort required to get back on track.'}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)] disabled:cursor-not-allowed transition-colors font-medium"
          >
            {phase === 'valueLost' ? 'Next: Cost to Restart' : 'Calculate Cost of Delay'}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}


